import React, {createContext, useContext, useMemo} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {getProcedureToolId} from 'llm/procedure-tool-library/procedureToolTypes';
import {getProcedureTypeForStudyTypeAllowNull} from 'studies/TypedStudyUtils';

import type {
  ProcedureRunPlansMapping,
  ProcedureRunPlansMappingVariables,
} from './__generated/ProcedureRunPlansMapping';
import type {StudyTypeMapping, StudyTypeMappingVariables} from './__generated/StudyTypeMapping';
import {PROCEDURE_RUN_PLANS_QUERY, STUDY_TYPE_QUERY} from './ProcedureToolMappingQuery';

export interface ProcedureToolMappingContextValue {
  isLoading: boolean;
  getProcedureType: (id: string) => ProcedureType | undefined;
}

const ProcedureToolMappingContext = createContext<ProcedureToolMappingContextValue | null>(null);

interface ProcedureToolMappingProviderProps {
  toolCalls: ReadonlyArray<ProgressToolCall>;
  children: React.ReactNode;
}

function useProcedureTypeMappingForToolCalls(
  toolCalls: ReadonlyArray<ProgressToolCall>
): ProcedureToolMappingContextValue {
  const {prpIds, studyIds} = useMemo(() => {
    const prp: Array<string> = [];
    const study: Array<string> = [];
    for (const toolCall of toolCalls) {
      const id = getProcedureToolId(toolCall.tool, toolCall.args);
      if (id == null) {
        continue;
      }
      if (id.startsWith('prp_')) {
        prp.push(id);
      } else if (id.startsWith('stdy_')) {
        study.push(id);
      }
    }
    return {prpIds: [...new Set(prp)], studyIds: [...new Set(study)]};
  }, [toolCalls]);

  const prpResult = useSkippableBenchlingQuery<ProcedureRunPlansMapping, ProcedureRunPlansMappingVariables>(
    PROCEDURE_RUN_PLANS_QUERY,
    {
      variables: {ids: prpIds},
      fetchPolicy: 'cache-first',
      skip: prpIds.length === 0,
    }
  );

  const studyResult = useSkippableBenchlingQuery<StudyTypeMapping, StudyTypeMappingVariables>(
    STUDY_TYPE_QUERY,
    {
      variables: {ids: studyIds},
      fetchPolicy: 'cache-first',
      skip: studyIds.length === 0,
    }
  );

  const isLoading =
    (prpIds.length > 0 && prpResult.type === QueryResultType.PENDING) ||
    (studyIds.length > 0 && studyResult.type === QueryResultType.PENDING);

  const prpData = prpResult.type === QueryResultType.SUCCESS ? prpResult.data : undefined;
  const studyData = studyResult.type === QueryResultType.SUCCESS ? studyResult.data : undefined;

  const procedureTypeById = useMemo((): Map<string, ProcedureType> => {
    const map = new Map<string, ProcedureType>();

    if (prpData) {
      for (const node of prpData.procedureRunPlans) {
        if (node.__typename !== 'ProcedureRunPlanNode') {
          continue;
        }
        const proxy = node.procedureFlowchartConfigVersionProxy;
        if (proxy?.__typename === 'ProcedureFlowchartConfigVersionProxyNode') {
          map.set(node.id, proxy.procedure.procedureType);
        }
      }
    }

    if (studyData) {
      for (const study of studyData.studies) {
        if (study.__typename !== 'StudyNode') {
          continue;
        }
        const procedureType = getProcedureTypeForStudyTypeAllowNull(study.studySchema.studyType);
        if (procedureType != null) {
          map.set(study.id, procedureType);
        }
      }
    }

    return map;
  }, [prpData, studyData]);

  return useMemo(
    (): ProcedureToolMappingContextValue => ({
      isLoading,
      getProcedureType: (id: string) => procedureTypeById.get(id),
    }),
    [isLoading, procedureTypeById]
  );
}

export function ProcedureToolMappingProvider({
  toolCalls,
  children,
}: ProcedureToolMappingProviderProps): JSX.Element {
  const contextValue = useProcedureTypeMappingForToolCalls(toolCalls);

  return (
    <ProcedureToolMappingContext.Provider value={contextValue}>
      {children}
    </ProcedureToolMappingContext.Provider>
  );
}

/**
 * Returns null if used outside of a ProcedureToolMappingProvider; consumers
 * should fall back to the generic display name in that case.
 */
export function useProcedureToolMappingOptional(): ProcedureToolMappingContextValue | null {
  return useContext(ProcedureToolMappingContext);
}
